/**
 * Safe interpreter for the `ChangeCallback` snippets carried on column
 * metadata.
 *
 * Metadata is not a trust boundary — it is edited through the admin surface and
 * arrives over the API — so the snippet is never handed to `eval` or
 * `new Function`, which would let whoever can write metadata run arbitrary code
 * in every user's browser. Instead the snippet is parsed here and interpreted
 * over a deliberately small, side-effect-free subset of JavaScript:
 *
 *   (record, newValue) => {
 *     if (newValue > 100) { record.Discount = record.Price * 0.9; }
 *     else record.Discount = null;
 *   }
 *
 * Supported: arrow and `function` callbacks, assignments to a parameter's
 * properties, `if`/`else`, ternaries, the comparison/logical/arithmetic
 * operators, and literals. Not supported (and rejected at parse time): calls,
 * `new`, loops, declarations, and any identifier that is not a declared
 * parameter. Callers treat a rejection the same way they have always treated a
 * broken callback — the handler is skipped and a warning is logged.
 */

type CallbackValue = any;
type CallbackScope = { [name: string]: CallbackValue };

export interface CompiledChangeCallback {
  (record: { [key: string]: any }, newValue: CallbackValue): void;
}

/** Property names that would reach the prototype chain. */
const forbiddenProperties = ["__proto__", "constructor", "prototype"];

/** Longest snippet we are willing to parse. */
const maxSourceLength = 4000;

const punctuation = [
  "===",
  "!==",
  "==",
  "!=",
  "<=",
  ">=",
  "&&",
  "||",
  "=>",
  "(",
  ")",
  "{",
  "}",
  "[",
  "]",
  ".",
  ",",
  ";",
  ":",
  "?",
  "<",
  ">",
  "=",
  "+",
  "-",
  "*",
  "/",
  "%",
  "!",
];

interface CallbackToken {
  type: "identifier" | "number" | "string" | "punctuation";
  value: string;
}

interface CallbackNode {
  type: string;
  [key: string]: any;
}

function tokenize(source: string): CallbackToken[] {
  const tokens: CallbackToken[] = [];
  let index = 0;

  while (index < source.length) {
    const character = source[index];

    if (/\s/.test(character)) {
      index++;
      continue;
    }

    if (/[A-Za-z_$]/.test(character)) {
      const match = source.slice(index).match(/^[A-Za-z0-9_$]+/)!;
      tokens.push({ type: "identifier", value: match[0] });
      index += match[0].length;
      continue;
    }

    if (/[0-9]/.test(character)) {
      const match = source.slice(index).match(/^[0-9]+(\.[0-9]+)?/)!;
      tokens.push({ type: "number", value: match[0] });
      index += match[0].length;
      continue;
    }

    if (character == '"' || character == "'") {
      // No escape sequences: the value is taken literally up to the next quote.
      const end = source.indexOf(character, index + 1);
      if (end < 0) throw new Error("Unterminated string literal");
      tokens.push({ type: "string", value: source.slice(index + 1, end) });
      index = end + 1;
      continue;
    }

    const operator = punctuation.find((candidate) =>
      source.startsWith(candidate, index)
    );
    if (!operator) throw new Error(`Unsupported character: ${character}`);

    tokens.push({ type: "punctuation", value: operator });
    index += operator.length;
  }

  return tokens;
}

class CallbackParser {
  private tokens: CallbackToken[];
  private position = 0;
  private parameters: string[] = [];

  constructor(tokens: CallbackToken[]) {
    this.tokens = tokens;
  }

  parseCallback(): { parameters: string[]; body: CallbackNode[] } {
    if (this.isPunctuation("(") || this.peek()?.type == "identifier") {
      if (this.isIdentifier("function")) {
        this.next();
        if (this.peek()?.type == "identifier") this.next(); // optional name
        this.parameters = this.parseParameters();
        const body = this.parseBlock();
        this.expectEnd();
        return { parameters: this.parameters, body };
      }

      this.parameters = this.isPunctuation("(")
        ? this.parseParameters()
        : [this.expectIdentifier()];
      this.expectPunctuation("=>");

      const body = this.isPunctuation("{")
        ? this.parseBlock()
        : [this.parseStatement()];
      this.expectEnd();
      return { parameters: this.parameters, body };
    }

    throw new Error("Callback must be an arrow or function expression");
  }

  private parseParameters(): string[] {
    this.expectPunctuation("(");
    const parameters: string[] = [];

    while (!this.isPunctuation(")")) {
      if (parameters.length > 0) this.expectPunctuation(",");
      parameters.push(this.expectIdentifier());
    }

    this.expectPunctuation(")");
    return parameters;
  }

  private parseBlock(): CallbackNode[] {
    this.expectPunctuation("{");
    const statements: CallbackNode[] = [];

    while (!this.isPunctuation("}")) {
      if (this.position >= this.tokens.length)
        throw new Error("Unterminated block");
      statements.push(this.parseStatement());
    }

    this.expectPunctuation("}");
    return statements;
  }

  private parseStatement(): CallbackNode {
    if (this.isPunctuation(";")) {
      this.next();
      return { type: "empty" };
    }

    if (this.isIdentifier("if")) {
      this.next();
      this.expectPunctuation("(");
      const test = this.parseExpression();
      this.expectPunctuation(")");
      const consequent = this.parseBody();

      let alternate: CallbackNode[] = [];
      if (this.isIdentifier("else")) {
        this.next();
        alternate = this.parseBody();
      }

      return { type: "if", test, consequent, alternate };
    }

    const target = this.parseMember();
    if (target.type != "member")
      throw new Error("Only property assignments are supported");

    this.expectPunctuation("=");
    const value = this.parseExpression();
    if (this.isPunctuation(";")) this.next();

    return { type: "assign", target, value };
  }

  private parseBody(): CallbackNode[] {
    return this.isPunctuation("{") ? this.parseBlock() : [this.parseStatement()];
  }

  private parseExpression(): CallbackNode {
    const test = this.parseBinary(0);
    if (!this.isPunctuation("?")) return test;

    this.next();
    const consequent = this.parseExpression();
    this.expectPunctuation(":");
    const alternate = this.parseExpression();

    return { type: "conditional", test, consequent, alternate };
  }

  /** Binary operators, lowest precedence level first. */
  private static readonly precedence: string[][] = [
    ["||"],
    ["&&"],
    ["===", "!==", "==", "!="],
    ["<=", ">=", "<", ">"],
    ["+", "-"],
    ["*", "/", "%"],
  ];

  private parseBinary(level: number): CallbackNode {
    if (level >= CallbackParser.precedence.length) return this.parseUnary();

    let left = this.parseBinary(level + 1);

    while (
      this.peek()?.type == "punctuation" &&
      CallbackParser.precedence[level].includes(this.peek()!.value)
    ) {
      const operator = this.next().value;
      const right = this.parseBinary(level + 1);
      left = { type: "binary", operator, left, right };
    }

    return left;
  }

  private parseUnary(): CallbackNode {
    if (
      this.peek()?.type == "punctuation" &&
      ["!", "-", "+"].includes(this.peek()!.value)
    ) {
      const operator = this.next().value;
      return { type: "unary", operator, argument: this.parseUnary() };
    }

    return this.parsePrimary();
  }

  private parsePrimary(): CallbackNode {
    const token = this.peek();
    if (!token) throw new Error("Unexpected end of callback");

    if (token.type == "number") {
      this.next();
      return { type: "literal", value: Number(token.value) };
    }

    if (token.type == "string") {
      this.next();
      return { type: "literal", value: token.value };
    }

    if (this.isPunctuation("(")) {
      this.next();
      const expression = this.parseExpression();
      this.expectPunctuation(")");
      return expression;
    }

    return this.parseMember();
  }

  private parseMember(): CallbackNode {
    const name = this.expectIdentifier();

    switch (name) {
      case "true":
        return { type: "literal", value: true };
      case "false":
        return { type: "literal", value: false };
      case "null":
        return { type: "literal", value: null };
      case "undefined":
        return { type: "literal", value: undefined };
    }

    if (!this.parameters.includes(name))
      throw new Error(`Unknown identifier: ${name}`);

    let node: CallbackNode = { type: "parameter", name };

    while (this.isPunctuation(".") || this.isPunctuation("[")) {
      const bracketed = this.next().value == "[";
      let property: string;

      if (bracketed) {
        const token = this.next();
        if (token?.type != "string")
          throw new Error("Only literal property names are supported");
        property = token.value;
        this.expectPunctuation("]");
      } else {
        property = this.expectIdentifier();
      }

      if (forbiddenProperties.includes(property))
        throw new Error(`Forbidden property: ${property}`);

      node = { type: "member", object: node, property };
    }

    // A bare parameter is still a valid expression, just not a valid target.
    return node;
  }

  private peek(): CallbackToken | undefined {
    return this.tokens[this.position];
  }

  private next(): CallbackToken {
    const token = this.tokens[this.position];
    if (!token) throw new Error("Unexpected end of callback");
    this.position++;
    return token;
  }

  private isPunctuation(value: string): boolean {
    const token = this.peek();
    return token?.type == "punctuation" && token.value == value;
  }

  private isIdentifier(value: string): boolean {
    const token = this.peek();
    return token?.type == "identifier" && token.value == value;
  }

  private expectPunctuation(value: string): void {
    if (!this.isPunctuation(value))
      throw new Error(`Expected '${value}' in callback`);
    this.next();
  }

  private expectIdentifier(): string {
    const token = this.peek();
    if (token?.type != "identifier") throw new Error("Expected an identifier");
    this.next();
    return token.value;
  }

  private expectEnd(): void {
    if (this.position < this.tokens.length)
      throw new Error("Unexpected trailing input in callback");
  }
}

function evaluateNode(node: CallbackNode, scope: CallbackScope): CallbackValue {
  switch (node.type) {
    case "literal":
      return node.value;

    case "parameter":
      return scope[node.name];

    case "member": {
      const object = evaluateNode(node.object, scope);
      if (object == null) return undefined;
      return (object as CallbackScope)[node.property];
    }

    case "unary": {
      const argument = evaluateNode(node.argument, scope);
      switch (node.operator) {
        case "!":
          return !argument;
        case "-":
          return -argument;
        default:
          return +argument;
      }
    }

    case "conditional":
      return evaluateNode(node.test, scope)
        ? evaluateNode(node.consequent, scope)
        : evaluateNode(node.alternate, scope);

    case "binary": {
      const left = evaluateNode(node.left, scope);

      // Short-circuit before touching the right-hand side, as JavaScript does.
      if (node.operator == "&&") return left && evaluateNode(node.right, scope);
      if (node.operator == "||") return left || evaluateNode(node.right, scope);

      const right = evaluateNode(node.right, scope);
      switch (node.operator) {
        case "===":
          return left === right;
        case "!==":
          return left !== right;
        case "==":
          return left == right;
        case "!=":
          return left != right;
        case "<":
          return left < right;
        case ">":
          return left > right;
        case "<=":
          return left <= right;
        case ">=":
          return left >= right;
        case "+":
          return left + right;
        case "-":
          return left - right;
        case "*":
          return left * right;
        case "/":
          return left / right;
        default:
          return left % right;
      }
    }

    default:
      throw new Error(`Unsupported expression: ${node.type}`);
  }
}

function executeStatements(
  statements: CallbackNode[],
  scope: CallbackScope
): void {
  statements.forEach((statement) => {
    switch (statement.type) {
      case "empty":
        return;

      case "if":
        executeStatements(
          evaluateNode(statement.test, scope)
            ? statement.consequent
            : statement.alternate,
          scope
        );
        return;

      case "assign": {
        const object = evaluateNode(statement.target.object, scope);
        if (object == null)
          throw new Error("Cannot assign to a property of null");
        (object as CallbackScope)[statement.target.property] = evaluateNode(
          statement.value,
          scope
        );
        return;
      }

      default:
        throw new Error(`Unsupported statement: ${statement.type}`);
    }
  });
}

/**
 * Parses a metadata `ChangeCallback` snippet into a handler. Throws when the
 * snippet falls outside the supported subset — callers log the same
 * "misconfigured change handler" warning they already log today.
 */
export function compileChangeCallback(
  source: string
): CompiledChangeCallback {
  if (source.length > maxSourceLength)
    throw new Error("Change callback is too long to parse");

  const { parameters, body } = new CallbackParser(
    tokenize(source)
  ).parseCallback();

  return (record, newValue) => {
    const scope: CallbackScope = {};
    if (parameters[0]) scope[parameters[0]] = record;
    if (parameters[1]) scope[parameters[1]] = newValue;

    executeStatements(body, scope);
  };
}
